/**
 * AI管理模块配置
 * 统一管理AI管理模块的路由、菜单、权限配置
 * @author AI Assistant
 * @date 2024-12-19
 */

import type { ModuleConfig } from '@/types/module-config'

/**
 * AI管理模块配置
 */
const moduleConfig: ModuleConfig = {
  id: 'aimanagement',
  name: 'AI管理',
  description: 'AI管理模块，包含AI工作流和AI知识库管理',
  version: '1.0.0',
  enabled: true,

  // 路由配置
  routes: [
    {
      path: '/aimanagement',
      name: 'Aimanagement',
      component: '@/layouts/index.vue',
      redirect: '/aimanagement/aiflow',
      meta: {
        title: 'AI管理',
        icon: 'Grid',
        roles: ['admin', 'super_admin'],
        alwaysShow: true,
        order: 100
      },
      children: [
        {
          path: 'aiflow',
          name: 'AiFlow',
          component: '@/modules/aimanagement/pages/aiflow.vue',
          meta: {
            title: 'AI工作流',
            icon: 'Connection',
            permissions: ['aimanagement:aiflow:view'],
            keepAlive: true
          }
        },
        {
          path: 'aiknowledge',
          name: 'AiKnowledge',
          component: '@/modules/aimanagement/pages/aiknowledge.vue',
          meta: {
            title: 'AI知识库',
            icon: 'Collection',
            permissions: ['aimanagement:aiknowledge:view'],
            keepAlive: true
          }
        },
        {
          path: 'workflow-designer/:id?',
          name: 'WorkflowDesigner',
          component: '@/modules/aimanagement/pages/workflow-designer.vue',
          meta: {
            title: '工作流设计器',
            icon: 'Edit',
            keepAlive: false,
            hideInMenu: true, // 不在菜单中显示，通过按钮跳转
            layout: 'blank', // 使用空白布局
            layoutConfig: {
              fullscreen: true, // 启用全屏模式
              background: 'default',
              padding: '0'
            }
          }
        },
        {
          path: 'workflow-designer-v2/:id?',
          name: 'WorkflowDesignerV2',
          component: '@/modules/aimanagement/pages/workflow-designer-v2.vue',
          meta: {
            title: '工作流设计器 V2',
            icon: 'SetUp',
            keepAlive: false,
            hideInMenu: true,
            layout: 'blank',
            layoutConfig: {
              fullscreen: true,
              background: 'default',
              padding: '0'
            }
          }
        },
        {
          path: 'knowledge-detail/:id',
          name: 'KnowledgeDetail',
          component: '@/modules/aimanagement/pages/knowledge-detail.vue',
          meta: {
            title: '知识库详情',
            icon: 'Document',
            keepAlive: false,
            hideInMenu: true, // 不在菜单中显示，通过按钮跳转
            permissions: ['aimanagement:aiknowledge:detail']
          }
        },
        {
          path: 'document-editor/:id',
          name: 'DocumentEditor',
          component: '@/modules/aimanagement/pages/document-editor.vue',
          meta: {
            title: '文档编辑器',
            icon: 'EditPen',
            keepAlive: false,
            hideInMenu: true, // 不在菜单中显示，通过按钮跳转
            permissions: ['aimanagement:aiknowledge:detail']
          }
        },
        {
          path: 'document-view/:knowledgeId/:documentId',
          name: 'DocumentView',
          component: '@/modules/aimanagement/pages/document-editor.vue',
          meta: {
            title: '文档查看',
            icon: 'View',
            keepAlive: false,
            hideInMenu: true, // 不在菜单中显示，通过按钮跳转
            permissions: ['aimanagement:aiknowledge:detail'],
            viewMode: 'preview' // 设置为预览模式
          }
        },
        {
          path: 'document-upload/:id',
          name: 'DocumentUpload',
          component: '@/modules/aimanagement/pages/document-upload.vue',
          meta: {
            title: '上传文档',
            icon: 'Upload',
            keepAlive: false,
            hideInMenu: true, // 不在菜单中显示，通过按钮跳转
            permissions: ['aimanagement:aiknowledge:upload']
          }
        },
        {
          path: 'knowledge-test/:id',
          name: 'KnowledgeTest',
          component: '@/modules/aimanagement/pages/knowledge-test.vue',
          meta: {
            title: '知识库检索测试',
            icon: 'Lightning',
            keepAlive: false,
            hideInMenu: true, // 不在菜单中显示，通过按钮跳转
            permissions: ['aimanagement:aiknowledge:test']
          }
        },
        // ==================== MCP管理 ====================
        {
          path: 'mcp',
          name: 'McpManagement',
          component: '@/modules/aimanagement/pages/mcp.vue',
          meta: {
            title: 'MCP管理',
            icon: 'Switch',
            permissions: ['aimanagement:mcp:view'],
            keepAlive: true
          }
        },
        {
          path: 'mcp-detail/:id',
          name: 'McpDetail',
          component: '@/modules/aimanagement/pages/mcp-detail.vue',
          meta: {
            title: 'MCP服务器详情',
            icon: 'Document',
            keepAlive: false,
            hideInMenu: true,
            permissions: ['aimanagement:mcp:view']
          }
        },
        // ==================== Agent管理 ====================
        {
          path: 'agent',
          name: 'AgentManagement',
          component: '@/modules/aimanagement/pages/agent.vue',
          meta: {
            title: 'Agent管理',
            icon: 'UserFilled',
            permissions: ['aimanagement:agent:view'],
            keepAlive: true
          }
        },
        {
          path: 'agent-detail/:id',
          name: 'AgentDetail',
          component: '@/modules/aimanagement/pages/agent-detail.vue',
          meta: {
            title: 'Agent详情',
            icon: 'User',
            keepAlive: false,
            hideInMenu: true,
            permissions: ['aimanagement:agent:view']
          }
        },
        {
          path: 'agent-execute/:id',
          name: 'AgentExecute',
          component: '@/modules/aimanagement/pages/agent-execute.vue',
          meta: {
            title: 'Agent执行调试',
            icon: 'VideoPlay',
            keepAlive: false,
            hideInMenu: true,
            permissions: ['aimanagement:agent:execute']
          }
        },
        {
          path: 'agent-execution/:executionId',
          name: 'AgentExecution',
          component: '@/modules/aimanagement/pages/agent-execution.vue',
          meta: {
            title: '执行结果',
            icon: 'Document',
            keepAlive: false,
            hideInMenu: true,
            permissions: ['aimanagement:agent:view']
          }
        },
        // ==================== LLM模型管理 ====================
        {
          path: 'llm-models',
          name: 'LLMModelManagement',
          component: '@/modules/aimanagement/pages/llm-models.vue',
          meta: {
            title: 'LLM模型管理',
            icon: 'Cpu',
            permissions: ['aimanagement:llm-models:view'],
            keepAlive: true
          }
        }
      ]
    }
  ],

  // 导航配置
  navigation: {
    name: 'aimanagement',
    title: 'AI管理',
    icon: 'Grid',
    order: 100,
    items: [
      {
        id: 'aimanagement',
        title: 'AI管理',
        path: '/aimanagement',
        icon: 'Grid',
        order: 0,
        permissions: ['aimanagement:view'],
        module: 'aimanagement',
        meta: {
          title: 'AI管理',
          keepAlive: false,
          hideInMenu: true
        }
      },
      {
        id: 'aimanagement-aiflow',
        title: 'AI工作流',
        path: '/aimanagement/aiflow',
        icon: 'Connection',
        order: 1,
        permissions: ['aimanagement:aiflow:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/aiflow.vue',
        meta: {
          title: 'AI工作流',
          keepAlive: true,
          parent: 'aimanagement'
        }
      },
      {
        id: 'aimanagement-aiknowledge',
        title: 'AI知识库',
        path: '/aimanagement/aiknowledge',
        icon: 'Collection',
        order: 2,
        permissions: ['aimanagement:aiknowledge:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/aiknowledge.vue',
        meta: {
          title: 'AI知识库',
          keepAlive: true,
          parent: 'aimanagement'
        }
      },
      {
        id: 'aimanagement-workflow-designer',
        title: '工作流设计器',
        path: '/aimanagement/workflow-designer',
        icon: 'SetUp',
        order: 3,
        permissions: ['aimanagement:workflow:design'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/workflow-designer.vue',
        hidden: true,
        meta: {
          title: '工作流设计器',
          keepAlive: true,
          hideInMenu: true
        }
      },
      {
        id: 'aimanagement-workflow-designer-v2',
        title: '工作流设计器 V2',
        path: '/aimanagement/workflow-designer-v2',
        icon: 'SetUp',
        order: 4,
        permissions: ['aimanagement:workflow:design'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/workflow-designer-v2.vue',
        hidden: true,
        meta: {
          title: '工作流设计器 V2',
          keepAlive: true,
          hideInMenu: true
        }
      },
      {
        id: 'aimanagement-knowledge-detail',
        title: '知识库详情',
        path: '/aimanagement/knowledge-detail/:id',
        icon: 'Document',
        order: 5,
        permissions: ['aimanagement:knowledge:detail'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/knowledge-detail.vue',
        hidden: true,
        meta: {
          title: '知识库详情',
          keepAlive: true,
          hideInMenu: true,
          parent: 'aimanagement-aiknowledge'
        }
      },
      {
        id: 'aimanagement-document-editor',
        title: '文档编辑器',
        path: '/aimanagement/document-editor/:id',
        icon: 'Edit',
        order: 6,
        permissions: ['aimanagement:document:edit'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/document-editor.vue',
        hidden: true,
        meta: {
          title: '文档编辑器',
          keepAlive: true,
          hideInMenu: true,
          parent: 'aimanagement-knowledge-detail'
        }
      },
      {
        id: 'aimanagement-document-upload',
        title: '上传文档',
        path: '/aimanagement/document-upload/:id',
        icon: 'Upload',
        order: 7,
        permissions: ['aimanagement:document:upload'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/document-upload.vue',
        hidden: true,
        meta: {
          title: '上传文档',
          keepAlive: true,
          hideInMenu: true,
          parent: 'aimanagement-knowledge-detail'
        }
      },
      {
        id: 'aimanagement-knowledge-test',
        title: '知识库检索测试',
        path: '/aimanagement/knowledge-test/:id',
        icon: 'Lightning',
        order: 8,
        permissions: ['aimanagement:knowledge:test'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/knowledge-test.vue',
        hidden: true,
        meta: {
          title: '知识库检索测试',
          keepAlive: false,
          hideInMenu: true,
          parent: 'aimanagement-aiknowledge'
        }
      },
      {
        id: 'aimanagement-document-view',
        title: '文档查看',
        path: '/aimanagement/document-view/:knowledgeId/:documentId',
        icon: 'View',
        order: 9,
        permissions: ['aimanagement:document:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/document-editor.vue',
        hidden: true,
        meta: {
          title: '文档查看',
          keepAlive: false,
          hideInMenu: true,
          parent: 'aimanagement-knowledge-detail',
          viewMode: 'preview'
        }
      },
      // ==================== MCP管理导航 ====================
      {
        id: 'aimanagement-mcp',
        title: 'MCP管理',
        path: '/aimanagement/mcp',
        icon: 'Switch',
        order: 3,
        permissions: ['aimanagement:mcp:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/mcp.vue',
        meta: {
          title: 'MCP管理',
          keepAlive: true,
          parent: 'aimanagement'
        }
      },
      {
        id: 'aimanagement-mcp-detail',
        title: 'MCP服务器详情',
        path: '/aimanagement/mcp-detail/:id',
        icon: 'Document',
        order: 10,
        permissions: ['aimanagement:mcp:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/mcp-detail.vue',
        hidden: true,
        meta: {
          title: 'MCP服务器详情',
          keepAlive: false,
          hideInMenu: true,
          parent: 'aimanagement-mcp'
        }
      },
      // ==================== Agent管理导航 ====================
      {
        id: 'aimanagement-agent',
        title: 'Agent管理',
        path: '/aimanagement/agent',
        icon: 'UserFilled',
        order: 4,
        permissions: ['aimanagement:agent:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/agent.vue',
        meta: {
          title: 'Agent管理',
          keepAlive: true,
          parent: 'aimanagement'
        }
      },
      {
        id: 'aimanagement-agent-detail',
        title: 'Agent详情',
        path: '/aimanagement/agent-detail/:id',
        icon: 'User',
        order: 11,
        permissions: ['aimanagement:agent:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/agent-detail.vue',
        hidden: true,
        meta: {
          title: 'Agent详情',
          keepAlive: false,
          hideInMenu: true,
          parent: 'aimanagement-agent'
        }
      },
      {
        id: 'aimanagement-agent-execute',
        title: 'Agent执行调试',
        path: '/aimanagement/agent-execute/:id',
        icon: 'VideoPlay',
        order: 12,
        permissions: ['aimanagement:agent:execute'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/agent-execute.vue',
        hidden: true,
        meta: {
          title: 'Agent执行调试',
          keepAlive: false,
          hideInMenu: true,
          parent: 'aimanagement-agent-detail'
        }
      },
      {
        id: 'aimanagement-agent-execution',
        title: '执行结果',
        path: '/aimanagement/agent-execution/:executionId',
        icon: 'Document',
        order: 13,
        permissions: ['aimanagement:agent:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/agent-execution.vue',
        hidden: true,
        meta: {
          title: '执行结果',
          keepAlive: false,
          hideInMenu: true,
          parent: 'aimanagement-agent-detail'
        }
      },
      // ==================== LLM模型管理导航 ====================
      {
        id: 'aimanagement-llm-models',
        title: 'LLM模型管理',
        path: '/aimanagement/llm-models',
        icon: 'Cpu',
        order: 5,
        permissions: ['aimanagement:llm-models:view'],
        module: 'aimanagement',
        component: '@/modules/aimanagement/pages/llm-models.vue',
        meta: {
          title: 'LLM模型管理',
          keepAlive: true,
          parent: 'aimanagement'
        }
      }
    ]
  },

  // 权限配置
  permissions: [
    {
      code: 'aimanagement:aiflow:view',
      name: '查看AI工作流',
      type: 'menu',
      description: '查看AI工作流列表',
      apiPath: '/aiflow'
    },
    {
      code: 'aimanagement:aiflow:add',
      name: '添加AI工作流',
      type: 'button',
      description: '添加新的AI工作流',
      apiPath: '/aiflow'
    },
    {
      code: 'aimanagement:aiflow:edit',
      name: '编辑AI工作流',
      type: 'button',
      description: '编辑AI工作流信息',
      apiPath: '/aiflow/:id'
    },
    {
      code: 'aimanagement:aiflow:delete',
      name: '删除AI工作流',
      type: 'button',
      description: '删除AI工作流',
      apiPath: '/aiflow/:id'
    },
    {
      code: 'aimanagement:aiflow:detail',
      name: '查看AI工作流详情',
      type: 'operation',
      description: '查看AI工作流详细信息',
      apiPath: '/aiflow/:id'
    },
    {
      code: 'aimanagement:aiflow:status',
      name: '修改AI工作流状态',
      type: 'operation',
      description: '启用/禁用AI工作流',
      apiPath: '/aiflow/:id/status'
    },
    {
      code: 'aimanagement:aiknowledge:view',
      name: '查看AI知识库',
      type: 'menu',
      description: '查看AI知识库列表',
      apiPath: '/api/knowledge/page'
    },
    {
      code: 'aimanagement:aiknowledge:add',
      name: '添加AI知识库',
      type: 'button',
      description: '添加新的AI知识库',
      apiPath: '/api/knowledge'
    },
    {
      code: 'aimanagement:aiknowledge:edit',
      name: '编辑AI知识库',
      type: 'button',
      description: '编辑AI知识库信息',
      apiPath: '/api/knowledge/{id}'
    },
    {
      code: 'aimanagement:aiknowledge:delete',
      name: '删除AI知识库',
      type: 'button',
      description: '删除AI知识库',
      apiPath: '/api/knowledge/{id}'
    },
    {
      code: 'aimanagement:aiknowledge:detail',
      name: '查看AI知识库详情',
      type: 'operation',
      description: '查看AI知识库详细信息',
      apiPath: '/api/knowledge/{id}'
    },
    {
      code: 'aimanagement:aiknowledge:status',
      name: '修改AI知识库状态',
      type: 'operation',
      description: '启用/禁用AI知识库',
      apiPath: '/api/knowledge/{id}'
    },
    {
      code: 'aimanagement:aiknowledge:edit',
      name: '编辑AI知识库',
      type: 'operation',
      description: '编辑知识库信息及文档管理（包含上传、编辑、删除、重新处理文档等操作）',
      apiPath: '/api/knowledge/{id}/*'
    },
    {
      code: 'aimanagement:aiknowledge:upload',
      name: '上传文档到知识库',
      type: 'operation',
      description: '向知识库上传新文档',
      apiPath: '/api/knowledge/{id}/document/upload'
    },
    {
      code: 'aimanagement:aiknowledge:test',
      name: '知识库检索测试',
      type: 'operation',
      description: '对知识库进行检索测试',
      apiPath: '/api/knowledge/retrieve'
    },
    // ==================== 文档管理权限 ====================
    {
      code: 'aimanagement:document:view',
      name: '查看文档',
      type: 'operation',
      description: '查看知识库中的文档列表和详情',
      apiPath: '/api/knowledge/{id}/documents/page'
    },
    {
      code: 'aimanagement:document:upload',
      name: '上传文档',
      type: 'operation',
      description: '向知识库上传文档文件',
      apiPath: '/api/knowledge/{id}/document/upload'
    },
    {
      code: 'aimanagement:document:load',
      name: '加载文档',
      type: 'operation',
      description: '加载已上传的文档进行处理（新接口上传时已自动处理）',
      apiPath: '/api/knowledge/{id}/document/upload'
    },
    {
      code: 'aimanagement:document:store',
      name: '存储文档',
      type: 'operation',
      description: '将处理后的文档存储到知识库（新接口上传时已自动处理）',
      apiPath: '/api/knowledge/{id}/document/upload'
    },
    {
      code: 'aimanagement:document:edit',
      name: '编辑文档',
      type: 'operation',
      description: '编辑文档内容和元数据',
      apiPath: '/api/knowledge/documents/{id}'
    },
    {
      code: 'aimanagement:document:delete',
      name: '删除文档',
      type: 'operation',
      description: '从知识库中删除文档',
      apiPath: '/api/knowledge/documents/{id}'
    },
    {
      code: 'aimanagement:document:chunk:view',
      name: '查看文档分块',
      type: 'operation',
      description: '查看文档的分块信息',
      apiPath: '/api/knowledge/{id}/chunks/page'
    },
    {
      code: 'aimanagement:document:chunk:edit',
      name: '编辑文档分块',
      type: 'operation',
      description: '编辑文档分块内容（新接口暂未提供）',
      apiPath: '/api/knowledge/chunks/{id}'
    },
    {
      code: 'aimanagement:document:reprocess',
      name: '重新处理文档',
      type: 'operation',
      description: '重新处理已上传的文档（新接口暂未提供）',
      apiPath: '/api/knowledge/documents/{id}/reprocess'
    },
    // ==================== MCP管理权限 ====================
    {
      code: 'aimanagement:mcp:view',
      name: '查看MCP管理',
      type: 'menu',
      description: '查看MCP服务器列表',
      apiPath: '/api/mcp/server/page'
    },
    {
      code: 'aimanagement:mcp:add',
      name: '创建MCP服务器',
      type: 'permission',
      description: '创建新的MCP服务器',
      apiPath: '/api/mcp/server'
    },
    {
      code: 'aimanagement:mcp:edit',
      name: '编辑MCP服务器',
      type: 'permission',
      description: '编辑MCP服务器信息',
      apiPath: '/api/mcp/server/{id}'
    },
    {
      code: 'aimanagement:mcp:delete',
      name: '删除MCP服务器',
      type: 'permission',
      description: '删除MCP服务器',
      apiPath: '/api/mcp/server/{id}'
    },
    {
      code: 'aimanagement:mcp:test',
      name: '测试MCP连接',
      type: 'permission',
      description: '测试MCP服务器连接',
      apiPath: '/api/mcp/server/{id}/test'
    },
    {
      code: 'aimanagement:mcp:sync',
      name: '同步MCP工具',
      type: 'permission',
      description: '同步MCP服务器工具列表',
      apiPath: '/api/mcp/server/{id}/sync'
    },
    {
      code: 'aimanagement:mcp:status',
      name: '修改MCP服务器状态',
      type: 'permission',
      description: '启用/禁用MCP服务器',
      apiPath: '/api/mcp/server/{id}/enable'
    },
    {
      code: 'aimanagement:mcp:invoke',
      name: '调用MCP工具',
      type: 'permission',
      description: '调用MCP工具进行测试',
      apiPath: '/api/mcp/tool/invoke'
    },
    // ==================== Agent管理权限 ====================
    {
      code: 'aimanagement:agent:view',
      name: '查看Agent管理',
      type: 'menu',
      description: '查看Agent列表',
      apiPath: '/api/agent/page'
    },
    {
      code: 'aimanagement:agent:add',
      name: '创建Agent',
      type: 'permission',
      description: '创建新的Agent',
      apiPath: '/api/agent'
    },
    {
      code: 'aimanagement:agent:edit',
      name: '编辑Agent',
      type: 'permission',
      description: '编辑Agent信息',
      apiPath: '/api/agent/{id}'
    },
    {
      code: 'aimanagement:agent:delete',
      name: '删除Agent',
      type: 'permission',
      description: '删除Agent',
      apiPath: '/api/agent/{id}'
    },
    {
      code: 'aimanagement:agent:execute',
      name: '执行Agent',
      type: 'permission',
      description: '执行Agent调试',
      apiPath: '/api/agent/{id}/execute'
    },
    {
      code: 'aimanagement:agent:publish',
      name: '发布Agent',
      type: 'permission',
      description: '发布Agent版本',
      apiPath: '/api/agent/{id}/publish'
    },
    {
      code: 'aimanagement:agent:status',
      name: '修改Agent状态',
      type: 'permission',
      description: '启用/停用/归档Agent',
      apiPath: '/api/agent/{id}/enable'
    },
    {
      code: 'aimanagement:agent:tool',
      name: '管理Agent工具',
      type: 'permission',
      description: '添加/删除Agent关联工具',
      apiPath: '/api/agent/{id}/tool'
    },
    {
      code: 'aimanagement:agent:subagent',
      name: '管理子Agent',
      type: 'permission',
      description: '设置Agent的子Agent',
      apiPath: '/api/agent/{id}/sub-agents'
    },
    {
      code: 'aimanagement:agent:param',
      name: '管理Agent输入参数',
      type: 'permission',
      description: '设置Agent的输入参数定义',
      apiPath: '/api/agent/{id}/input-params'
    },
    {
      code: 'aimanagement:agent:stream',
      name: '流式执行Agent',
      type: 'permission',
      description: '流式执行Agent获取实时输出',
      apiPath: '/api/agent/{id}/stream'
    },
    // ==================== LLM模型管理权限 ====================
    {
      code: 'aimanagement:llm-models:view',
      name: '查看LLM模型',
      type: 'menu',
      description: '查看LLM模型列表',
      apiPath: '/api/llm/model/list'
    },
    {
      code: 'aimanagement:llm-models:add',
      name: '创建LLM模型',
      type: 'permission',
      description: '创建新的LLM模型',
      apiPath: '/api/llm/model'
    },
    {
      code: 'aimanagement:llm-models:edit',
      name: '编辑LLM模型',
      type: 'permission',
      description: '编辑LLM模型配置',
      apiPath: '/api/llm/model/{llmCode}'
    },
    {
      code: 'aimanagement:llm-models:delete',
      name: '删除LLM模型',
      type: 'permission',
      description: '删除LLM模型',
      apiPath: '/api/llm/model/{llmCode}'
    },
    {
      code: 'aimanagement:llm-models:status',
      name: '修改LLM模型状态',
      type: 'permission',
      description: '启用/停用LLM模型',
      apiPath: '/api/llm/model/{llmCode}/toggle'
    }
  ]
}

export default moduleConfig
